import React, { createContext, useContext, useState, useCallback, useEffect } from "react";
import { tenantStorageKey, apiLogout } from "@/lib/api";

// === Role Types ===
export type MainRole = "admin" | "hr" | "user";
export type UserSubRole = "employee" | "line_manager" | "c_level";

export interface RoleConfig {
  mainRole: MainRole;
  subRole?: UserSubRole;
}

// === User ===
export interface AppUser {
  id: string;
  employeeId: string | null;
  isLineManager: boolean;
  fullProfileAccess: boolean;
  email: string;
  name: string;
  initials: string;
  role: RoleConfig;
  department: string;
  avatarColor: string; // gradient class
  avatarUrl?: string | null;
  accessMode: "full" | "limited" | "disabled";
}

// === Role display info ===
// eslint-disable-next-line react-refresh/only-export-components
export const MAIN_ROLE_INFO: Record<MainRole, { label: string; color: string; gradient: string }> = {
  admin: {
    label: "Admin",
    color: "hsl(0, 84%, 60%)",
    gradient: "from-red-500 to-orange-500",
  },
  hr: {
    label: "HR Manager",
    color: "hsl(263, 70%, 58%)",
    gradient: "from-purple-500 to-indigo-500",
  },
  user: {
    label: "User",
    color: "hsl(210, 92%, 55%)",
    gradient: "from-blue-500 to-cyan-500",
  },
};

// eslint-disable-next-line react-refresh/only-export-components
export const USER_SUB_ROLE_INFO: Record<UserSubRole, { label: string; color: string; gradient: string }> = {
  employee: {
    label: "Employee",
    color: "hsl(210, 92%, 55%)",
    gradient: "from-blue-400 to-blue-600",
  },
  line_manager: {
    label: "Line Manager",
    color: "hsl(142, 72%, 42%)",
    gradient: "from-green-400 to-emerald-600",
  },
  c_level: {
    label: "C-Level",
    color: "hsl(280, 80%, 65%)",
    gradient: "from-violet-500 to-purple-600",
  },
};

// === Auth helpers ===

const SUB_ROLE_I18N_KEY: Record<UserSubRole, string> = {
  employee: "roles.employee",
  line_manager: "roles.lineManager",
  c_level: "roles.cLevel",
};

const MAIN_ROLE_I18N_KEY: Record<MainRole, string> = {
  admin: "roles.admin",
  hr: "roles.hrManager",
  user: "roles.employee",
};

// eslint-disable-next-line react-refresh/only-export-components
export function getRoleI18nKey(role: RoleConfig): string {
  if (role.mainRole === "user" && role.subRole) {
    return SUB_ROLE_I18N_KEY[role.subRole];
  }
  return MAIN_ROLE_I18N_KEY[role.mainRole];
}

function getRoleLabelFromConfig(role: RoleConfig): string {
  if (role.mainRole === "user" && role.subRole) {
    return USER_SUB_ROLE_INFO[role.subRole].label;
  }
  return MAIN_ROLE_INFO[role.mainRole].label;
}

function getRoleGradientFromConfig(role: RoleConfig): string {
  if (role.mainRole === "user" && role.subRole) {
    return USER_SUB_ROLE_INFO[role.subRole].gradient;
  }
  return MAIN_ROLE_INFO[role.mainRole].gradient;
}

// === Context ===
interface AuthContextType {
  user: AppUser | null;
  login: (email: string, password: string) => Promise<{ success: boolean; error?: string; requires2FA?: boolean; tempToken?: string }>;
  completeTwoFactorLogin: (user: AppUser, token: string, tenantSlug: string) => void;
  logout: () => Promise<void>;
  getRoleLabel: () => string;
  getRoleGradient: () => string;
  updateUser: (updates: Partial<AppUser>) => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

function isTokenValid(): boolean {
  try {
    const token = localStorage.getItem(tenantStorageKey("token"));
    if (!token) return false;
    // Decode JWT payload (base64url) without verifying signature — just check expiry
    const payload = JSON.parse(atob(token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/")));
    return typeof payload.exp === "number" && payload.exp * 1000 > Date.now();
  } catch {
    return false;
  }
}

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AppUser | null>(() => {
    const stored = localStorage.getItem(tenantStorageKey("user"));
    if (stored) {
      try {
        // Only restore session if the stored JWT is still valid
        if (!isTokenValid()) {
          localStorage.removeItem(tenantStorageKey("user"));
          localStorage.removeItem(tenantStorageKey("token"));
          return null;
        }
        return JSON.parse(stored) as AppUser;
      } catch {
        return null;
      }
    }
    return null;
  });

  const updateUser = useCallback((updates: Partial<AppUser>) => {
    setUser(prev => {
      if (!prev) return null;
      const newUser = { ...prev, ...updates };
      localStorage.setItem(tenantStorageKey("user"), JSON.stringify(newUser));
      return newUser;
    });
  }, []);

  useEffect(() => {
    if (user?.email) {
      import("@/lib/api").then(({ apiGetEmployeeByEmail }) => {
        apiGetEmployeeByEmail(user.email!)
          .then((emp) => {
            if (emp && (emp.avatarUrl !== user.avatarUrl || emp.name !== user.name || emp.isLineManager !== user.isLineManager || emp.fullProfileAccess !== user.fullProfileAccess)) {
              updateUser({
                avatarUrl: emp.avatarUrl,
                name: emp.name,
                isLineManager: emp.isLineManager,
                fullProfileAccess: emp.fullProfileAccess,
              });
            }
          })
          .catch(() => {});
      });
    }
  }, [user?.email, user?.avatarUrl, user?.name, user?.isLineManager, user?.fullProfileAccess, updateUser]);

  const completeTwoFactorLogin = useCallback((loggedInUser: AppUser, token: string, tenantSlug: string) => {
    // Called after a successful 2FA verification — stores user/token identical
    // to the regular login path. apiVerify2FA already persists the token.
    setUser(loggedInUser);
    localStorage.setItem(tenantStorageKey("user"), JSON.stringify(loggedInUser));
    if (tenantSlug) localStorage.setItem("besthr_active_tenant", tenantSlug);
    if (token) localStorage.setItem(tenantStorageKey("token"), token);
  }, []);

  const login = useCallback(async (email: string, password: string): Promise<{ success: boolean; error?: string; requires2FA?: boolean; tempToken?: string }> => {
    try {
      const { apiLogin } = await import("@/lib/api");
      const result = await apiLogin(email, password);
      if ("requires2FA" in result && result.requires2FA) {
        // 2FA required — don't set user yet; return pending token to the caller
        return { success: true, requires2FA: true, tempToken: result.tempToken };
      }
      const { user: loggedInUser, token } = result as { user: AppUser; token: string };
      setUser(loggedInUser);
      localStorage.setItem(tenantStorageKey("user"), JSON.stringify(loggedInUser));
      // apiLogin already stores the token, but keep it in sync here too
      if (token) localStorage.setItem(tenantStorageKey("token"), token);
      return { success: true };
    } catch (err: any) {
      const message = err?.message || "login.errorNoAccount";
      return { success: false, error: message };
    }
  }, []);

  const logout = useCallback(async () => {
    // Server-side revocation — adds the JWT's jti to revoked_jwts so a
    // copied/leaked token cannot be reused even within its 24h lifetime.
    // Best-effort: network failures shouldn't block the local sign-out.
    try { await apiLogout(); } catch { /* ignore */ }

    setUser(null);
    localStorage.removeItem(tenantStorageKey("user"));
    localStorage.removeItem(tenantStorageKey("token"));
    // Also clear the active-tenant pointer so a different user signing in
    // on the same device starts at the tenant picker rather than landing
    // straight on the previous tenant's subdomain context.
    localStorage.removeItem("besthr_active_tenant");
  }, []);

  // Listen for session-expired events dispatched by apiFetch on 401 responses
  useEffect(() => {
    const handler = () => {
      // Don't log out if a superadmin impersonation session is currently active.
      // The impersonation flow injects its own credentials; a stray 401 from an
      // unrelated endpoint should not tear down the impersonated session.
      try {
        const impStored = localStorage.getItem("besthr_sa_impersonation");
        if (impStored) {
          const imp = JSON.parse(impStored);
          if (imp.token && new Date(imp.expiresAt) > new Date()) return;
        }
      } catch { /* ignore */ }
      setUser(null);
      localStorage.removeItem(tenantStorageKey("user"));
      localStorage.removeItem(tenantStorageKey("token"));
    };
    window.addEventListener("besthr:session-expired", handler);
    return () => window.removeEventListener("besthr:session-expired", handler);
  }, []);

  const getRoleLabel = useCallback(() => {
    if (!user) return "";
    return getRoleLabelFromConfig(user.role);
  }, [user]);

  const getRoleGradient = useCallback(() => {
    if (!user) return "";
    return getRoleGradientFromConfig(user.role);
  }, [user]);

  return (
    <AuthContext.Provider value={{ user, login, completeTwoFactorLogin, logout, getRoleLabel, getRoleGradient, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
